/**
 * The preparation progress stepper. A read-only projection of the release
 * document's per-step statuses (`done`/`next`/`pending`/`failed`) in the shared
 * canonical order, so the operator always sees where the release is — the merge →
 * capture → describe → review → publish → merge-back path — without reading logs.
 */
import { Step, StepLabel, Stepper, Typography } from '@mui/material';
import { PREPARE_STEP_ORDER, type PrepareReleaseDoc, type PrepareStepName } from '@finvari/deployer';

const LABELS: Record<PrepareStepName, string> = {
    merge: 'Merge main',
    capture: 'Capture PRs',
    describe: 'Draft announcement',
    review: 'Review',
    publish: 'Publish & tag',
    mergeBack: 'Merge back',
};

/**
 * `steps` is optional for the same reason `homeMode` reads it with `?.`: the
 * release document is written by several merge writes and by host builds older
 * than some of its fields, so a reader that indexed it unconditionally would
 * take the whole page down over a progress bar. No statuses reads as nothing
 * reported yet, which is what it means.
 */
export function StepperView({ steps }: { steps: PrepareReleaseDoc['steps'] | undefined }) {
    const statuses = steps ?? ({} as PrepareReleaseDoc['steps']);
    // The active step is the first one marked `next` (or `failed`); everything
    // before it is done, everything after is pending.
    const activeIndex = PREPARE_STEP_ORDER.findIndex((s) => statuses[s] === 'next' || statuses[s] === 'failed');

    return (
        <Stepper activeStep={activeIndex < 0 ? PREPARE_STEP_ORDER.length : activeIndex} alternativeLabel>
            {PREPARE_STEP_ORDER.map((step) => {
                const status = statuses[step];
                return (
                    <Step key={step} completed={status === 'done'}>
                        <StepLabel error={status === 'failed'}>
                            {LABELS[step]}
                            {status === 'failed' && (
                                <Typography variant="caption" color="error" display="block">
                                    failed — see the instruction log
                                </Typography>
                            )}
                        </StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );
}
